import { api } from '@ui/app/api';
import type {
    SourceConfig,
    Source,
    SourceType,
    HealthLakeConfig,
    ImagingSource,
    ImagingSourceListResponse,
    CreateImagingSourceDto,
    UpdateImagingSourceDto,
    ConnectionTestResponse,
    DatastoreListResponse,
    StudyListResponse,
    StudySearchParams,
    SeriesListResponse,
    InstanceListResponse,
    TestConnectionRequest,
    TestConnectionResponse,
} from '../types';
import { mapBackendToDisplay, mapDisplayToBackend, mapSourceStatus } from '../constants';
import { formatDateTime, formatDate } from '@packages/utils';

// HealthLake Preview Types
interface HealthLakeConnectionConfig {
    aws_access_key_id: string;
    aws_secret_access_key: string;
    aws_region: string;
    datastore_id: string;
    aws_session_token?: string;
}

interface HealthLakeDataPreviewRequest {
    resource: string;
    limit?: number;
    query_params?: Record<string, any>;
    timeout?: number;
    connection_config: HealthLakeConnectionConfig;
}

interface HealthLakeDataPreviewResponse {
    success: boolean;
    message: string;
    resource?: string;
    data?: any;
    record_count?: number;
}

interface SourceResponse {
    id: number;
    source_name: string;
    source_type: string;
    connection_config: SourceConfig;
    description?: string;
    status: string;
    docker_config: {
        image: string;
        tag?: string;
    };
    data_fetch_config?: {
        job_run_schedule?: string;
        data_format?: string;
        batch_size?: number;
        deidentify_document?: boolean;
    };
    error_handling_config?: Record<string, any>;
    other_config?: Record<string, any>;
    run_id?: string;
    created_at: string;
    updated_at: string;
    created_by: string;
    source_run_status?: string;
    next_run?: string;
    start_date?: string;
    end_date?: string;
}

function transformSourceToJob(source: SourceResponse): Source {
    return {
        id: source.id.toString(),
        sourceName: source.source_name,
        sourceType: mapBackendToDisplay(source.source_type),
        createdBy: source.created_by,
        createdAt: formatDateTime(source.created_at),
        updatedAt: formatDateTime(source.updated_at),
        lastVisit: formatDate(source.updated_at),
        status: mapSourceStatus(source.status, source.source_run_status),
        schedule: source.data_fetch_config?.job_run_schedule || null,
        configuration: source.connection_config,
    };
}

function transformJobToSource(
    job: Omit<Source, 'id' | 'createdBy' | 'createdAt' | 'updatedAt' | 'lastVisit' | 'status'>
) {
    if (!job.sourceType) {
        throw new Error('Source type is required');
    }

    if (!job.sourceName) {
        throw new Error('Source name is required');
    }

    // Backend will validate and sanitize the name, so just send it as-is
    const sourceType = mapDisplayToBackend(job.sourceType);

    return {
        source_name: job.sourceName,
        source_type: sourceType,
        connection_config: job.configuration || {},
        description: job.sourceName,
        status: 'active',
        data_fetch_config: {
            job_run_schedule: job.schedule || null,
        },
    };
}

// VLM Types
export interface AnalyzeInstanceDto {
    prompt?: string;
    model?: string;
    maxTokens?: number;
    temperature?: number;
    isDebug?: boolean;
}

export interface GeneratePromptDto {
    originalPrompt: string;
    modality?: string;
    sourceId?: string;
    studyUID?: string;
    seriesUID?: string;
    instanceUID?: string;
    datastoreId?: string;
}

export interface VlmAnalysisResponse {
    id: string;
    object: string;
    created: number;
    model: string;
    choices: {
        index: number;
        message: {
            role: string;
            content: string;
        };
        finish_reason: string;
    }[];
    usage: {
        prompt_tokens: number;
        completion_tokens: number;
        total_tokens: number;
    };
}

export const sourceRegistryApi = api.injectEndpoints({
    endpoints: builder => ({
        // ... previous endpoints ...
        getAllSources: builder.query<Source[], void>({
            query: () => ({
                url: '/source',
                method: 'GET',
            }),
            transformResponse: (response: SourceResponse[]) => {
                return response.map(transformSourceToJob);
            },
            providesTags: result =>
                result
                    ? [...result.map(({ id }) => ({ type: 'Sources' as const, id })), { type: 'Sources', id: 'LIST' }]
                    : [{ type: 'Sources', id: 'LIST' }],
        }),

        getSourceById: builder.query<Source, number>({
            query: id => ({
                url: '/source',
                method: 'GET',
                params: { source_id: id },
            }),
            transformResponse: (response: SourceResponse) => {
                return transformSourceToJob(response);
            },
            providesTags: (result, error, id) => [{ type: 'Sources', id }],
        }),

        createSource: builder.mutation<
            Source,
            Omit<Source, 'id' | 'createdBy' | 'createdAt' | 'updatedAt' | 'lastVisit' | 'status'>
        >({
            query: body => ({
                url: '/source',
                method: 'POST',
                body: transformJobToSource(body),
            }),
            transformResponse: (response: SourceResponse) => {
                return transformSourceToJob(response);
            },
            invalidatesTags: [{ type: 'Sources', id: 'LIST' }],
        }),

        updateSource: builder.mutation<Source, { id: number; data: Partial<Omit<Source, 'id'>> }>({
            query: ({ id, data }) => ({
                url: '/source',
                method: 'PATCH',
                params: { source_id: id },
                body: data.sourceName
                    ? transformJobToSource(
                          data as Omit<Source, 'id' | 'createdBy' | 'createdAt' | 'updatedAt' | 'lastVisit' | 'status'>
                      )
                    : {
                          status: data.status?.toLowerCase(),
                          data_fetch_config: data.schedule
                              ? {
                                    job_run_schedule: data.schedule,
                                }
                              : undefined,
                      },
            }),
            transformResponse: (response: SourceResponse) => {
                return transformSourceToJob(response);
            },
            invalidatesTags: (result, error, { id }) => [
                { type: 'Sources', id },
                { type: 'Sources', id: 'LIST' },
            ],
        }),

        deleteSource: builder.mutation<void, string>({
            query: sourceName => ({
                url: '/source',
                method: 'DELETE',
                params: { source_name: sourceName },
            }),
            invalidatesTags: [{ type: 'Sources', id: 'LIST' }],
        }),

        testSourceConnection: builder.mutation<TestConnectionResponse, TestConnectionRequest>({
            query: body => ({
                url: '/source/connectivity',
                method: 'POST',
                body,
            }),
        }),

        // HealthLake data preview endpoint
        previewHealthLakeData: builder.mutation<
            HealthLakeDataPreviewResponse,
            {
                config: HealthLakeConfig;
                resource: string;
                limit?: number;
            }
        >({
            query: ({ config, resource, limit = 5 }) => ({
                url: '/source/healthlake/data-preview',
                method: 'POST',
                body: {
                    resource,
                    limit,
                    timeout: 30,
                    connection_config: {
                        aws_access_key_id: config.accessKeyId,
                        aws_secret_access_key: config.secretAccessKey,
                        aws_region: config.region,
                        datastore_id: config.datastoreId,
                    },
                } as HealthLakeDataPreviewRequest,
            }),
        }),

        // === Medical Imaging Source CRUD ===
        getImagingSources: builder.query<ImagingSource[], void>({
            query: () => ({
                url: '/medical-imaging/sources',
                method: 'GET',
            }),
            transformResponse: (response: ImagingSourceListResponse) => response.sources,
            providesTags: result =>
                result
                    ? [
                          ...result.map(({ id }) => ({ type: 'ImagingSources' as const, id })),
                          { type: 'ImagingSources', id: 'LIST' },
                      ]
                    : [{ type: 'ImagingSources', id: 'LIST' }],
        }),

        getImagingSourceById: builder.query<ImagingSource, string>({
            query: id => ({
                url: `/medical-imaging/sources/${id}`,
                method: 'GET',
            }),
            providesTags: (_, __, id) => [{ type: 'ImagingSources', id }],
        }),

        createImagingSource: builder.mutation<ImagingSource, CreateImagingSourceDto>({
            query: body => ({
                url: '/medical-imaging/sources',
                method: 'POST',
                body,
            }),
            invalidatesTags: [{ type: 'ImagingSources', id: 'LIST' }],
        }),

        updateImagingSource: builder.mutation<ImagingSource, { id: string; data: UpdateImagingSourceDto }>({
            query: ({ id, data }) => ({
                url: `/medical-imaging/sources/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_, __, { id }) => [
                { type: 'ImagingSources', id },
                { type: 'ImagingSources', id: 'LIST' },
            ],
        }),

        deleteImagingSource: builder.mutation<{ message: string }, string>({
            query: id => ({
                url: `/medical-imaging/sources/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'ImagingSources', id: 'LIST' }],
        }),

        testImagingSourceConnection: builder.mutation<ConnectionTestResponse, string>({
            query: id => ({
                url: `/medical-imaging/sources/${id}/test`,
                method: 'POST',
            }),
        }),

        testImagingCredentials: builder.mutation<ConnectionTestResponse, CreateImagingSourceDto>({
            query: body => ({
                url: '/medical-imaging/test-credentials',
                method: 'POST',
                body,
            }),
        }),

        // === Datastores (AWS) ===
        getDatastores: builder.query<DatastoreListResponse, string>({
            query: sourceId => ({
                url: `/medical-imaging/sources/${sourceId}/datastores`,
                method: 'GET',
            }),
        }),

        // === Studies (QIDO-RS) ===
        searchStudies: builder.query<StudyListResponse, StudySearchParams>({
            query: ({ sourceId, datastoreId, ...params }) => ({
                url: `/medical-imaging/sources/${sourceId}/studies`,
                method: 'GET',
                params: {
                    datastoreId,
                    ...params,
                },
            }),
            providesTags: (_, __, { sourceId }) => [{ type: 'Studies', id: sourceId }],
        }),

        // === Series ===
        getSeries: builder.query<
            SeriesListResponse,
            { sourceId: string; studyUID: string; datastoreId?: string; limit?: number; offset?: number }
        >({
            query: ({ sourceId, studyUID, datastoreId, limit, offset }) => ({
                url: `/medical-imaging/sources/${sourceId}/studies/${studyUID}/series`,
                method: 'GET',
                params: { datastoreId, limit, offset },
            }),
        }),

        // === Instances ===
        getInstances: builder.query<
            InstanceListResponse,
            {
                sourceId: string;
                studyUID: string;
                seriesUID: string;
                datastoreId?: string;
                limit?: number;
                offset?: number;
            }
        >({
            query: ({ sourceId, studyUID, seriesUID, datastoreId, limit, offset }) => ({
                url: `/medical-imaging/sources/${sourceId}/studies/${studyUID}/series/${seriesUID}/instances`,
                method: 'GET',
                params: { datastoreId, limit, offset },
            }),
        }),

        // === VLM Analysis ===
        analyzeInstance: builder.mutation<
            VlmAnalysisResponse,
            {
                sourceId: string;
                studyUID: string;
                seriesUID: string;
                instanceUID: string;
                body: AnalyzeInstanceDto;
                frameNumber?: number;
                datastoreId?: string;
            }
        >({
            query: ({ sourceId, studyUID, seriesUID, instanceUID, body, frameNumber, datastoreId }) => ({
                url: `/medical-imaging/sources/${sourceId}/studies/${studyUID}/series/${seriesUID}/instances/${instanceUID}/analyze`,
                method: 'POST',
                body,
                params: { frameNumber, datastoreId },
            }),
        }),

        getExamplePrompts: builder.query<string[], string | undefined>({
            query: modality => ({
                url: '/medical-imaging/prompts/examples',
                method: 'GET',
                params: { modality },
            }),
        }),

        previewPrompt: builder.mutation<{ enhancedPrompt: string }, GeneratePromptDto>({
            query: body => ({
                url: '/medical-imaging/prompts/preview',
                method: 'POST',
                body,
            }),
        }),
    }),
});

export const {
    useGetAllSourcesQuery,
    useGetSourceByIdQuery,
    useCreateSourceMutation,
    useUpdateSourceMutation,
    useDeleteSourceMutation,
    useTestSourceConnectionMutation,
    usePreviewHealthLakeDataMutation,
    useGetImagingSourcesQuery,
    useGetImagingSourceByIdQuery,
    useCreateImagingSourceMutation,
    useUpdateImagingSourceMutation,
    useDeleteImagingSourceMutation,
    useTestImagingSourceConnectionMutation,
    useTestImagingCredentialsMutation,
    useGetDatastoresQuery,
    useSearchStudiesQuery,
    useGetSeriesQuery,
    useGetInstancesQuery,
    useAnalyzeInstanceMutation,
    useGetExamplePromptsQuery,
    usePreviewPromptMutation,
} = sourceRegistryApi;

// Parameter types for URL helper functions
export interface InstanceUrlParams {
    sourceId: string;
    studyUID: string;
    seriesUID: string;
    instanceUID: string;
    datastoreId?: string;
}

export interface ThumbnailUrlParams extends InstanceUrlParams {
    size?: number;
}

export interface RenderedImageUrlParams extends InstanceUrlParams {
    frameNumber?: number;
}

// Helper function to build thumbnail URL
export function getThumbnailUrl({
    sourceId,
    studyUID,
    seriesUID,
    instanceUID,
    size = 256,
    datastoreId,
}: ThumbnailUrlParams): string {
    const params = new URLSearchParams({ size: String(size) });
    if (datastoreId) params.append('datastoreId', datastoreId);
    return `/api/medical-imaging/sources/${sourceId}/studies/${studyUID}/series/${seriesUID}/instances/${instanceUID}/thumbnail?${params}`;
}

// Helper function to build rendered image URL
export function getRenderedImageUrl({
    sourceId,
    studyUID,
    seriesUID,
    instanceUID,
    frameNumber = 1,
    datastoreId,
}: RenderedImageUrlParams): string {
    const params = new URLSearchParams({ frameNumber: String(frameNumber) });
    if (datastoreId) params.append('datastoreId', datastoreId);
    return `/api/medical-imaging/sources/${sourceId}/studies/${studyUID}/series/${seriesUID}/instances/${instanceUID}/rendered?${params}`;
}

// Helper function to build DICOM file download URL
export function getDicomFileUrl({
    sourceId,
    studyUID,
    seriesUID,
    instanceUID,
    datastoreId,
}: InstanceUrlParams): string {
    const params = datastoreId ? `?datastoreId=${datastoreId}` : '';
    return `/api/medical-imaging/sources/${sourceId}/studies/${studyUID}/series/${seriesUID}/instances/${instanceUID}/dicom${params}`;
}
